import assertExhaustive from '@bybeam/platform-lib/utilities/assertExhaustive';
import {
  EligibilityQuestion as IEligibilityQuestion,
  EligibilityQuestionType,
} from '@bybeam/platform-types';
import Dropdown from '@platform-ui-common/components/Dropdown/index.js';
import { IconType } from '@platform-ui-common/components/Icon/Icon.types.js';
import Icon from '@platform-ui-common/components/Icon/index.js';
import Markdown from '@platform-ui-common/components/Markdown/index.js';
import RadioGroup from '@platform-ui-common/components/RadioGroup/index.js';
import TextInput from '@platform-ui-common/components/TextInput/index.js';
import {
  DispatchFieldChange,
  GenericFormData,
  fieldUpdate,
} from '@platform-ui-common/hooks/useForm/index.js';
import replacePlaceholders, {
  PlaceholderSource,
} from '@platform-ui-common/utilities/placeholder.js';
import { RadioButtonOptions } from '@platform-ui/application/utils/index.js';
import AMIComponent from './components/AmiComponent/index.js';

interface EligibilityQuestionProps {
  question: IEligibilityQuestion;
  formData: GenericFormData;
  dispatch: DispatchFieldChange<GenericFormData>;
  errors: GenericFormData;
  placeholders?: PlaceholderSource;
}

function renderQuestion({
  question,
  formData,
  dispatch,
  errors,
  placeholders,
}: EligibilityQuestionProps): JSX.Element {
  switch (question.type) {
    case EligibilityQuestionType.AMIComponent:
      return (
        <AMIComponent dispatch={dispatch} formData={formData} question={question} errors={errors} />
      );
    case EligibilityQuestionType.Content:
      return (
        <div className="flex gap-2">
          {question.props?.icon && <Icon type={question.props.icon as IconType} size="LG" />}
          <Markdown>{replacePlaceholders(question.props.content, placeholders)}</Markdown>
        </div>
      );
    case EligibilityQuestionType.Dropdown:
      return (
        <Dropdown
          id={question.key}
          value={formData[question.key] ?? ''}
          onChange={(value): void => dispatch(fieldUpdate(question.key, value))}
          items={question.props.items}
          label={question.props.label}
          error={errors?.[question.key] as string}
          fullWidth
        />
      );
    case EligibilityQuestionType.TextInput:
      return (
        <TextInput
          id={question.key}
          fullWidth
          label={question.props.label}
          value={formData[question.key] ?? ''}
          onChange={(value): void => dispatch(fieldUpdate(question.key, value))}
          type={question.props.format}
          error={errors?.[question.key] as string}
        />
      );
    case EligibilityQuestionType.YesOrNo:
      return (
        <RadioGroup
          name={question.key}
          selectedValue={formData[question.key] ?? ''}
          onChange={(value): void => dispatch(fieldUpdate(question.key, value))}
          items={RadioButtonOptions.YesOrNo}
          error={!!errors?.[question.key]}
          helperText={errors?.[question.key] as string}
        />
      );
    default:
      return assertExhaustive(question);
  }
}

export default function EligibilityQuestion(props: EligibilityQuestionProps) {
  return (
    // TODO cleanup styles
    <div className="flex flex-col w-full gap-4 py-4 px-5 lg:px-8">
      <div className="flex flex-col lg:grow">
        {props.question.copy && (
          <Markdown>{replacePlaceholders(props.question.copy, props.placeholders)}</Markdown>
        )}
      </div>
      {renderQuestion(props)}
    </div>
  );
}
